import { useState, useEffect, useCallback } from "react";
import { Users, Tag, CreditCard, BarChart3, LogOut, Search, Plus, X, RefreshCw, Shield, TrendingUp, FileQuestion, Trash2, Download } from "lucide-react";
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import axios from "axios";
import { API } from "../constants";

const AdminPanel = ({ onBack }) => {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem('adminKey') || '');
  const [authenticated, setAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState('stats');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState({ users: [], total: 0, page: 1, pages: 1 });
  const [promoCodes, setPromoCodes] = useState([]);
  const [transactions, setTransactions] = useState({ transactions: [], total: 0, page: 1, pages: 1 });
  const [growth, setGrowth] = useState(null);
  const [unsupportedFiles, setUnsupportedFiles] = useState({ files: [], total: 0, page: 1, pages: 1 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [showCreatePromo, setShowCreatePromo] = useState(false);
  const [newPromo, setNewPromo] = useState({ code: '', days_access: 30, max_uses: '' });

  const headers = { 'X-Admin-Key': adminKey };

  const fetchStats = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers });
      setStats(res.data);
    } catch { setError("Error cargando estadísticas"); }
  }, [adminKey]);

  const fetchUsers = useCallback(async (page = 1) => {
    try {
      const params = { page, limit: 50 };
      if (searchQuery) params.search = searchQuery;
      if (planFilter) params.plan = planFilter;
      const res = await axios.get(`${API}/admin/users`, { headers, params });
      setUsers(res.data);
    } catch { setError("Error cargando usuarios"); }
  }, [adminKey, searchQuery, planFilter]);

  const fetchPromos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/promo/list`, { headers });
      setPromoCodes(res.data.codes || []);
    } catch { setError("Error cargando códigos"); }
  }, [adminKey]);

  const fetchTransactions = useCallback(async (page = 1) => {
    try {
      const res = await axios.get(`${API}/admin/transactions`, { headers, params: { page, limit: 50 } });
      setTransactions(res.data);
    } catch { setError("Error cargando transacciones"); }
  }, [adminKey]);

  const fetchGrowth = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/growth`, { headers, params: { days: 90 } });
      setGrowth(res.data);
    } catch { setError("Error cargando métricas de crecimiento"); }
  }, [adminKey]);

  const fetchUnsupportedFiles = useCallback(async (page = 1) => {
    try {
      const res = await axios.get(`${API}/admin/unsupported-files`, { headers, params: { page, limit: 20 } });
      setUnsupportedFiles(res.data);
    } catch { setError("Error cargando archivos"); }
  }, [adminKey]);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      await axios.get(`${API}/admin/stats`, { headers: { 'X-Admin-Key': adminKey } });
      localStorage.setItem('adminKey', adminKey);
      setAuthenticated(true);
    } catch {
      setError("Clave incorrecta");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authenticated) {
      fetchStats();
      fetchUsers();
      fetchPromos();
      fetchTransactions();
      fetchGrowth();
      fetchUnsupportedFiles();
    }
  }, [authenticated]);

  useEffect(() => {
    if (authenticated) fetchUsers();
  }, [searchQuery, planFilter]);

  const handleUpdatePlan = async (userId, newPlan) => {
    try {
      await axios.patch(`${API}/admin/users/${userId}/plan`, null, { headers, params: { plan: newPlan } });
      fetchUsers();
      fetchStats();
    } catch (e) {
      setError(e.response?.data?.detail || "Error actualizando plan");
    }
  };

  const handleCreatePromo = async () => {
    try {
      const data = { code: newPromo.code, days_access: parseInt(newPromo.days_access) };
      if (newPromo.max_uses) data.max_uses = parseInt(newPromo.max_uses);
      await axios.post(`${API}/promo/create`, data, { headers });
      setShowCreatePromo(false);
      setNewPromo({ code: '', days_access: 30, max_uses: '' });
      fetchPromos();
    } catch (e) {
      setError(e.response?.data?.detail || "Error creando código");
    }
  };

  const handleDeletePromo = async (code) => {
    if (!window.confirm(`¿Desactivar código ${code}?`)) return;
    try {
      await axios.delete(`${API}/promo/${code}`, { headers });
      fetchPromos();
    } catch (e) {
      setError(e.response?.data?.detail || "Error desactivando código");
    }
  };

  const handleDeleteFile = async (filename) => {
    if (!window.confirm(`¿Eliminar archivo ${filename}?`)) return;
    try {
      await axios.delete(`${API}/admin/unsupported-files/${encodeURIComponent(filename)}`, { headers });
      fetchUnsupportedFiles();
    } catch (e) {
      setError(e.response?.data?.detail || "Error eliminando archivo");
    }
  };

  const handleDownloadFile = async (filename) => {
    try {
      const res = await axios.get(`${API}/admin/unsupported-files/${encodeURIComponent(filename)}/download`, {
        headers, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setError("Error descargando archivo");
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminKey');
    setAuthenticated(false);
    setAdminKey('');
  };

  const chartTooltipStyle = {
    contentStyle: { background: '#0d0d0f', border: '1px solid #374151', borderRadius: '8px' },
    labelStyle: { color: '#9ca3af' },
  };

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-[#0a0a0b] flex items-center justify-center p-6">
        <div className="bg-[#0d0d0f] border border-gray-800 rounded-2xl p-10 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">Admin Panel</h1>
          <p className="text-gray-500 text-sm mb-6">Introduce la clave de administrador</p>
          <input
            type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
            placeholder="Clave de administrador"
            className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white mb-4 focus:outline-none focus:border-red-500"
            data-testid="admin-key-input"
          />
          {error && <p className="text-red-400 text-sm mb-4">{error}</p>}
          <button onClick={handleLogin} disabled={loading} data-testid="admin-login-btn"
            className="w-full py-3 bg-red-600 hover:bg-red-700 rounded-lg text-white font-medium transition-colors disabled:opacity-50">
            {loading ? 'Verificando...' : 'Acceder'}
          </button>
          <button onClick={onBack} className="mt-4 text-gray-500 hover:text-gray-300 text-sm transition-colors">Volver</button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'stats', label: 'Dashboard', icon: BarChart3 },
    { id: 'growth', label: 'Crecimiento', icon: TrendingUp },
    { id: 'users', label: 'Usuarios', icon: Users },
    { id: 'promos', label: 'Códigos', icon: Tag },
    { id: 'transactions', label: 'Pagos', icon: CreditCard },
    { id: 'files', label: 'Archivos', icon: FileQuestion },
  ];

  return (
    <div className="min-h-screen bg-[#0a0a0b] text-white">
      <header className="border-b border-gray-800 bg-[#0d0d0f]/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Shield className="w-6 h-6 text-red-400" />
            <h1 className="text-xl font-bold hidden sm:block">Admin Panel</h1>
            <div className="flex gap-1 overflow-x-auto">
              {tabs.map(tab => (
                <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs sm:text-sm whitespace-nowrap transition-colors ${activeTab === tab.id ? 'bg-red-600 text-white' : 'text-gray-400 hover:bg-gray-800'}`}>
                  <tab.icon className="w-4 h-4" /> <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={onBack} className="text-gray-400 hover:text-white text-sm hidden sm:block">Volver</button>
            <button onClick={handleLogout} className="p-2 hover:bg-gray-800 rounded-lg"><LogOut className="w-4 h-4 text-gray-400" /></button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {error && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg flex items-center justify-between">
            <span className="text-red-400 text-sm">{error}</span>
            <button onClick={() => setError(null)}><X className="w-4 h-4 text-red-400" /></button>
          </div>
        )}

        {/* Stats Tab */}
        {activeTab === 'stats' && stats && (
          <div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-5">
                <p className="text-gray-400 text-sm mb-1">Total Usuarios</p>
                <p className="text-3xl font-bold text-white">{stats.users.total}</p>
                <p className="text-gray-500 text-xs mt-1">{stats.users.verified} verificados</p>
              </div>
              <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-5">
                <p className="text-gray-400 text-sm mb-1">Premium</p>
                <p className="text-3xl font-bold text-cyan-400">{stats.users.premium}</p>
                <p className="text-gray-500 text-xs mt-1">{stats.users.promo} con promo</p>
              </div>
              <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-5">
                <p className="text-gray-400 text-sm mb-1">Ingresos Totales</p>
                <p className="text-3xl font-bold text-emerald-400">{stats.revenue.total}€</p>
                <p className="text-gray-500 text-xs mt-1">{stats.revenue.transactions_count} transacciones</p>
              </div>
              <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-5">
                <p className="text-gray-400 text-sm mb-1">Ingresos Mes</p>
                <p className="text-3xl font-bold text-emerald-400">{stats.revenue.this_month}€</p>
                <p className="text-gray-500 text-xs mt-1">{stats.promos.active} promos activos</p>
              </div>
            </div>
            <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6">
              <h3 className="text-lg font-semibold mb-4">Distribución de Planes</h3>
              <div className="flex gap-4">
                {[
                  { label: 'Free', count: stats.users.free, color: 'bg-gray-500' },
                  { label: 'Premium', count: stats.users.premium, color: 'bg-cyan-500' },
                  { label: 'Promo', count: stats.users.promo, color: 'bg-purple-500' },
                ].map(p => (
                  <div key={p.label} className="flex items-center gap-3">
                    <div className={`w-3 h-3 rounded-full ${p.color}`}></div>
                    <span className="text-gray-300 text-sm">{p.label}: <strong>{p.count}</strong></span>
                  </div>
                ))}
              </div>
              {stats.users.total > 0 && (
                <div className="mt-4 flex h-3 rounded-full overflow-hidden bg-gray-800">
                  {stats.users.free > 0 && <div className="bg-gray-500" style={{ width: `${(stats.users.free / stats.users.total) * 100}%` }}></div>}
                  {stats.users.premium > 0 && <div className="bg-cyan-500" style={{ width: `${(stats.users.premium / stats.users.total) * 100}%` }}></div>}
                  {stats.users.promo > 0 && <div className="bg-purple-500" style={{ width: `${(stats.users.promo / stats.users.total) * 100}%` }}></div>}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Growth Tab */}
        {activeTab === 'growth' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Métricas de Crecimiento</h2>
              <button onClick={fetchGrowth} className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg"><RefreshCw className="w-4 h-4 text-gray-400" /></button>
            </div>

            {growth && (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                  <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-5">
                    <p className="text-gray-400 text-sm mb-1">Usuarios Totales</p>
                    <p className="text-3xl font-bold text-white">{growth.total_users}</p>
                  </div>
                  <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-5">
                    <p className="text-gray-400 text-sm mb-1">Usuarios Premium</p>
                    <p className="text-3xl font-bold text-cyan-400">{growth.premium_users}</p>
                  </div>
                  <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-5">
                    <p className="text-gray-400 text-sm mb-1">Tasa de Conversión</p>
                    <p className="text-3xl font-bold text-emerald-400">{growth.conversion_rate}%</p>
                  </div>
                  <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-5">
                    <p className="text-gray-400 text-sm mb-1">Archivos No Soportados</p>
                    <p className="text-3xl font-bold text-amber-400">{growth.unsupported_files_count}</p>
                  </div>
                </div>

                {/* Registrations Chart */}
                <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6 mb-6">
                  <h3 className="text-base font-semibold mb-4 text-gray-300">Registros por Día (últimos 90 días)</h3>
                  {growth.registrations.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={growth.registrations}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} allowDecimals={false} />
                        <Tooltip {...chartTooltipStyle} />
                        <Bar dataKey="registros" fill="#06b6d4" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-10">No hay registros en los últimos 90 días</p>
                  )}
                </div>

                {/* Cumulative Users Chart */}
                <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6 mb-6">
                  <h3 className="text-base font-semibold mb-4 text-gray-300">Usuarios Acumulados</h3>
                  {growth.cumulative_users.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <LineChart data={growth.cumulative_users}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} allowDecimals={false} />
                        <Tooltip {...chartTooltipStyle} />
                        <Line type="monotone" dataKey="total" stroke="#10b981" strokeWidth={2} dot={false} />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-10">Sin datos de crecimiento</p>
                  )}
                </div>

                {/* Revenue Chart */}
                <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6 mb-6">
                  <h3 className="text-base font-semibold mb-4 text-gray-300">Ingresos por Día</h3>
                  {growth.revenue.length > 0 ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={growth.revenue}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} />
                        <Tooltip {...chartTooltipStyle} />
                        <Bar dataKey="ingresos" fill="#10b981" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-10">Sin ingresos registrados</p>
                  )}
                </div>

                {/* Messages Chart */}
                <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6">
                  <h3 className="text-base font-semibold mb-4 text-gray-300">Mensajes de Contacto por Día</h3>
                  {growth.messages.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={growth.messages}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
                        <XAxis dataKey="date" tick={{ fill: '#6b7280', fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                        <YAxis tick={{ fill: '#6b7280', fontSize: 11 }} allowDecimals={false} />
                        <Tooltip {...chartTooltipStyle} />
                        <Bar dataKey="mensajes" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-gray-500 text-center py-10">Sin mensajes de contacto</p>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="flex items-center gap-4 mb-6">
              <div className="flex items-center gap-2 bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 flex-1 max-w-md">
                <Search className="w-4 h-4 text-gray-500" />
                <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por nombre o email..." className="bg-transparent text-white text-sm outline-none flex-1" data-testid="admin-search-users" />
              </div>
              <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}
                className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-gray-300" data-testid="admin-plan-filter">
                <option value="">Todos los planes</option>
                <option value="free">Free</option>
                <option value="monthly">Mensual</option>
                <option value="yearly">Anual</option>
                <option value="lifetime">Lifetime</option>
                <option value="promo">Promo</option>
              </select>
              <button onClick={() => fetchUsers()} className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg"><RefreshCw className="w-4 h-4 text-gray-400" /></button>
              <span className="text-gray-500 text-sm">{users.total} usuarios</span>
            </div>
            <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-900/50">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Nombre</th>
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Plan</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Verificado</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Registro</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Último Login</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {users.users.map((u) => (
                    <tr key={u.id} className="border-b border-gray-800/50 hover:bg-gray-900/30">
                      <td className="py-3 px-4 text-white font-medium">{u.name}</td>
                      <td className="py-3 px-4 text-gray-300">{u.email}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          u.plan === 'free' ? 'bg-gray-500/20 text-gray-400' :
                          u.plan === 'promo' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-cyan-500/20 text-cyan-400'
                        }`}>{u.plan}</span>
                      </td>
                      <td className="py-3 px-4 text-center">
                        <span className={u.email_verified ? 'text-emerald-400' : 'text-red-400'}>{u.email_verified ? 'Sí' : 'No'}</span>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-400 text-xs">{u.created_at?.slice(0, 10)}</td>
                      <td className="py-3 px-4 text-center text-gray-400 text-xs">{u.last_login?.slice(0, 10) || '-'}</td>
                      <td className="py-3 px-4 text-center">
                        <select value={u.plan} onChange={(e) => handleUpdatePlan(u.id, e.target.value)}
                          className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-gray-300">
                          <option value="free">free</option>
                          <option value="monthly">monthly</option>
                          <option value="yearly">yearly</option>
                          <option value="lifetime">lifetime</option>
                          <option value="promo">promo</option>
                        </select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {users.pages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: users.pages }, (_, i) => (
                  <button key={i} onClick={() => fetchUsers(i + 1)}
                    className={`px-3 py-1 rounded text-sm ${users.page === i + 1 ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Promos Tab */}
        {activeTab === 'promos' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Códigos Promocionales</h2>
              <button onClick={() => setShowCreatePromo(true)} data-testid="create-promo-btn"
                className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 rounded-lg text-sm font-medium transition-colors">
                <Plus className="w-4 h-4" /> Crear Código
              </button>
            </div>
            {showCreatePromo && (
              <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-6 mb-6">
                <h3 className="text-white font-medium mb-4">Nuevo Código Promocional</h3>
                <div className="flex gap-4 items-end flex-wrap">
                  <div>
                    <label className="text-gray-400 text-xs block mb-1">Código</label>
                    <input type="text" value={newPromo.code} onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
                      placeholder="EJEMPLO20" className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm" data-testid="new-promo-code" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs block mb-1">Días de acceso</label>
                    <input type="number" value={newPromo.days_access} onChange={(e) => setNewPromo({ ...newPromo, days_access: e.target.value })}
                      className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm w-24" data-testid="new-promo-days" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs block mb-1">Usos máx. (vacío = ilimitado)</label>
                    <input type="number" value={newPromo.max_uses} onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                      placeholder="∞" className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm w-24" data-testid="new-promo-max" />
                  </div>
                  <button onClick={handleCreatePromo} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 rounded-lg text-sm font-medium transition-colors">Crear</button>
                  <button onClick={() => setShowCreatePromo(false)} className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg text-sm transition-colors">Cancelar</button>
                </div>
              </div>
            )}
            <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-900/50">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Código</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Días</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Usos</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Estado</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Creado</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {promoCodes.map((p) => (
                    <tr key={p.code} className="border-b border-gray-800/50 hover:bg-gray-900/30">
                      <td className="py-3 px-4 text-white font-mono font-bold">{p.code}</td>
                      <td className="py-3 px-4 text-center text-gray-300">{p.days_access}</td>
                      <td className="py-3 px-4 text-center text-gray-300">{p.current_uses || 0}/{p.max_uses || '∞'}</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${p.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}`}>
                          {p.is_active ? 'Activo' : 'Inactivo'}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-400 text-xs">{p.created_at?.slice(0, 10)}</td>
                      <td className="py-3 px-4 text-center">
                        {p.is_active && (
                          <button onClick={() => handleDeletePromo(p.code)} className="text-red-400 hover:text-red-300 text-xs">Desactivar</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {promoCodes.length === 0 && (
                    <tr><td colSpan="6" className="py-8 text-center text-gray-500">No hay códigos promocionales</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Transactions Tab */}
        {activeTab === 'transactions' && (
          <div>
            <h2 className="text-lg font-semibold mb-6">Historial de Transacciones</h2>
            <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-800 bg-gray-900/50">
                    <th className="text-left py-3 px-4 text-gray-400 font-medium">Email</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Plan</th>
                    <th className="text-right py-3 px-4 text-gray-400 font-medium">Monto</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Estado</th>
                    <th className="text-center py-3 px-4 text-gray-400 font-medium">Fecha</th>
                  </tr>
                </thead>
                <tbody>
                  {transactions.transactions.map((tx) => (
                    <tr key={tx.id || `${tx.user_email}-${tx.created_at}`} className="border-b border-gray-800/50 hover:bg-gray-900/30">
                      <td className="py-3 px-4 text-gray-300">{tx.user_email}</td>
                      <td className="py-3 px-4 text-center">
                        <span className="px-2 py-1 bg-cyan-500/20 text-cyan-400 rounded text-xs font-medium">{tx.plan}</span>
                      </td>
                      <td className="py-3 px-4 text-right text-white font-medium">{tx.amount}€</td>
                      <td className="py-3 px-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          tx.payment_status === 'paid' ? 'bg-emerald-500/20 text-emerald-400' :
                          tx.payment_status === 'pending' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-red-500/20 text-red-400'
                        }`}>{tx.payment_status}</span>
                      </td>
                      <td className="py-3 px-4 text-center text-gray-400 text-xs">{tx.created_at?.slice(0, 10)}</td>
                    </tr>
                  ))}
                  {transactions.transactions.length === 0 && (
                    <tr><td colSpan="5" className="py-8 text-center text-gray-500">No hay transacciones</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Unsupported Files Tab */}
        {activeTab === 'files' && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">Archivos de Exchanges No Soportados</h2>
              <div className="flex items-center gap-3">
                <span className="text-gray-500 text-sm">{unsupportedFiles.total} archivos</span>
                <button onClick={() => fetchUnsupportedFiles()} className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg"><RefreshCw className="w-4 h-4 text-gray-400" /></button>
              </div>
            </div>

            {unsupportedFiles.files.length > 0 ? (
              <div className="space-y-4">
                {unsupportedFiles.files.map((f) => (
                  <div key={f.id || `${f.filename}-${f.uploaded_at}`} className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-5">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-white font-medium">{f.filename}</h3>
                        <p className="text-gray-500 text-xs mt-1">
                          {f.user_name} ({f.user_email}) · {(f.file_size / 1024).toFixed(1)} KB · {f.uploaded_at?.slice(0, 16).replace('T', ' ')}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          f.status === 'pending_review' ? 'bg-amber-500/20 text-amber-400' : 'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {f.status === 'pending_review' ? 'Pendiente' : f.status}
                        </span>
                        <button onClick={() => handleDownloadFile(f.filename)}
                          className="p-2 hover:bg-cyan-500/20 rounded-lg transition-colors" title="Descargar" data-testid={`download-file-${f.filename}`}>
                          <Download className="w-4 h-4 text-cyan-400" />
                        </button>
                        <button onClick={() => handleDeleteFile(f.filename)}
                          className="p-2 hover:bg-red-500/20 rounded-lg transition-colors" title="Eliminar">
                          <Trash2 className="w-4 h-4 text-red-400" />
                        </button>
                      </div>
                    </div>
                    {f.columns_info && (
                      <div className="bg-gray-900/50 rounded-lg p-3">
                        <p className="text-gray-500 text-xs mb-1 font-medium">Columnas / Contenido detectado:</p>
                        <p className="text-gray-400 text-xs font-mono break-all leading-relaxed">{f.columns_info.slice(0, 500)}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="bg-[#0d0d0f] border border-gray-800 rounded-xl p-10 text-center">
                <FileQuestion className="w-12 h-12 text-gray-700 mx-auto mb-3" />
                <p className="text-gray-500">No hay archivos de exchanges no soportados</p>
                <p className="text-gray-600 text-xs mt-1">Aparecerán aquí cuando un usuario suba un archivo de un exchange que no reconozcamos</p>
              </div>
            )}

            {unsupportedFiles.pages > 1 && (
              <div className="flex justify-center gap-2 mt-4">
                {Array.from({ length: unsupportedFiles.pages }, (_, i) => (
                  <button key={i} onClick={() => fetchUnsupportedFiles(i + 1)}
                    className={`px-3 py-1 rounded text-sm ${unsupportedFiles.page === i + 1 ? 'bg-red-600 text-white' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                    {i + 1}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminPanel;
